import React, { useState, useEffect } from 'react';
import { Select, Loader, Group, Button, Modal, TextInput, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { env } from '../../../utils/env';

interface CategorySelectorProps {
    groupId: string;
    accessToken: string;
    value: string | undefined;
    onChange: (value: string) => void;
}

interface Category {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
}

export const CategorySelector = ({
    groupId,
    accessToken,
    value,
    onChange
}: CategorySelectorProps) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryDescription, setNewCategoryDescription] = useState('');

    // Fetch categories
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/category`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(Array.isArray(data) ? data : []);
            } else {
                console.warn('Failed to fetch categories:', response.status);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [accessToken, groupId]);

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) {
            notifications.show({
                title: 'Validation Error',
                message: 'Category name is required',
                color: 'red',
            });
            return;
        }

        try {
            setCreating(true);
            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/category`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newCategoryName.trim(),
                    description: newCategoryDescription.trim() || undefined,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create category');
            }

            const newCategory = await response.json();

            // Update categories list
            setCategories(prev => [...prev, newCategory]);

            // Select the newly created category
            onChange(newCategory.id.toString());

            // Reset form and close modal
            setNewCategoryName('');
            setNewCategoryDescription('');
            setShowCreateModal(false);

            notifications.show({
                title: 'Category Created',
                message: `Successfully created "${newCategory.name}"`,
                color: 'green',
            });

        } catch (error) {
            console.error('Error creating category:', error);
            notifications.show({
                title: 'Creation Failed',
                message: error instanceof Error ? error.message : 'Failed to create category',
                color: 'red',
            });
        } finally {
            setCreating(false);
        }
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setNewCategoryName('');
        setNewCategoryDescription('');
    };

    return (
        <>
            <Group align="flex-end" gap="xs">
                <Select
                    style={{ flex: 1 }}
                    label="Category"
                    placeholder="Select a category"
                    data={categories.map(category => ({
                        value: category.id.toString(),
                        label: category.name,
                    }))}
                    value={value}
                    onChange={(val) => onChange(val || '')}
                    rightSection={loading ? <Loader size="xs" /> : null}
                    disabled={loading}
                />
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateModal(true)}
                    disabled={loading}
                    style={{ marginBottom: 0 }}
                >
                    +
                </Button>
            </Group>

            <Modal
                opened={showCreateModal}
                onClose={handleModalClose}
                title="Create New Category"
                centered
                size="md"
            >
                <Stack gap="md">
                    <TextInput
                        label="Category Name"
                        placeholder="e.g., Food & Dining, Transportation"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        required
                        disabled={creating}
                    />
                    <TextInput
                        label="Description (Optional)"
                        placeholder="Brief description of this category"
                        value={newCategoryDescription}
                        onChange={(e) => setNewCategoryDescription(e.target.value)}
                        disabled={creating}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="subtle"
                            onClick={handleModalClose}
                            disabled={creating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateCategory}
                            loading={creating}
                        >
                            Create Category
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}; 